import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import HeroAutoplay from "@/components/HeroAutoplay";
import Top10Rail from "@/components/Top10Rail";
import ContentRow from "@/components/ContentRow";
import EmailCapture from "@/components/EmailCapture";
import ReasonsGrid from "@/components/ReasonsGrid";
import FAQAccordion from "@/components/FAQAccordion";
import StickyBottomCTA from "@/components/StickyBottomCTA";
import ExitIntentModal from "@/components/ExitIntentModal";
import { NotFound } from "@/pages/ErrorPages";
import { Play, Plus, Info } from "lucide-react";
import { formatMoney } from "@/lib/money";

export default function Storefront() {
  const { slug, sp } = useStore();
  const navigate = useNavigate();
  const { user, status } = useAuth();
  const [data, setData] = useState(null);
  const [pageStatus, setPageStatus] = useState("loading");

  useEffect(() => {
    let alive = true;
    setPageStatus("loading");
    api.get(`/public/tenants/${slug}/landing`)
      .then((r) => alive && (setData(r.data), setPageStatus("ready")))
      .catch(() => alive && setPageStatus("not_found"));
    return () => { alive = false; };
  }, [slug]);

  if (pageStatus === "loading") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] grid place-items-center text-white/40 text-sm uppercase tracking-widest" data-testid="storefront-loading">
        Loading…
      </div>
    );
  }
  if (pageStatus === "not_found" || !data) return <NotFound />;

  const { tenant, hero, cheapest_plan, top10_week, new_releases, trending_movies, reels_preview, genres, faqs } = data;
  const themeColor = tenant.theme_color;
  const accentColor = tenant.accent_color;
  const isOwnSubscriber = status === "authenticated" && user && user.tenant_id === tenant.id;

  const cheapestLabel = cheapest_plan
    ? `${formatMoney(cheapest_plan.amount, cheapest_plan.currency)} / ${cheapest_plan.interval === "yearly" ? "year" : "month"}`
    : "Indie-friendly pricing";

  const heroItems = hero ? [hero] : [];

  return (
    <StorefrontLayout
      tenant={tenant}
      currentUser={isOwnSubscriber ? user : null}
      heroBleed
    >
      {/* HERO — Apple TV style autoplay */}
      <HeroAutoplay
        items={heroItems}
        themeColor={themeColor}
        accentColor={accentColor}
        heightClass="h-[60vh] md:h-[78vh] lg:h-[88vh] min-h-[420px]"
        testid="storefront-hero"
      >
        {(active) => (
          <>
            <button
              onClick={() => navigate(sp(`/signup?intent=watch&content=${active.slug}`))}
              className="inline-flex items-center gap-2 px-6 md:px-8 h-12 md:h-14 text-sm md:text-base font-bold uppercase tracking-[0.2em] bg-white text-black hover:bg-white/90 transition-colors"
              data-testid="storefront-hero-play"
            >
              <Play className="w-4 h-4 fill-black" /> Play
            </button>
            <button
              onClick={() => navigate(sp(`/signup?intent=add&content=${active.slug}`))}
              className="inline-flex items-center gap-2 px-5 md:px-7 h-12 md:h-14 text-sm md:text-base font-bold uppercase tracking-[0.2em] border border-white/40 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white transition-colors"
              data-testid="storefront-hero-add"
            >
              <Plus className="w-4 h-4" /> Add
            </button>
            <Link
              to={sp(`/title/${active.slug}`)}
              className="inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.25em] text-white/75 hover:text-white px-3 md:px-4 h-12 md:h-14"
              data-testid="storefront-hero-info"
            >
              <Info className="w-4 h-4" /> More info
            </Link>
          </>
        )}
      </HeroAutoplay>

      {/* CONVERSION BAND — Netflix-style email capture */}
      <section className="relative mt-8 md:mt-14 z-10 px-6 md:px-12 pb-12 md:pb-16">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center md:text-left max-w-3xl mx-auto md:mx-0">
            <h2 className="font-display font-black text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.05]" data-testid="storefront-conversion-headline">
              Unlimited stories.<br />
              One studio.
              <span className="block mt-2 md:mt-3" style={{ color: accentColor }}>{tenant.name}.</span>
            </h2>
            <p className="mt-5 text-white/70 text-base md:text-lg font-light max-w-xl mx-auto md:mx-0" data-testid="storefront-conversion-subhead">
              Starts at <span className="text-white font-medium">{cheapestLabel}</span>. Cancel anytime.
            </p>
            <div className="mt-8 flex flex-col items-center md:items-start gap-3">
              <EmailCapture tenantSlug={slug} accentColor={accentColor} themeColor={themeColor} testid="storefront-email-capture" />
              <Link to={sp(`/login`)} className="text-xs uppercase tracking-[0.3em] text-white/55 hover:text-white" data-testid="storefront-signin-link">
                Already a member? Sign in →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* RAILS */}
      <div className="max-w-[1500px] mx-auto pt-2 pb-10 space-y-6">
        {top10_week && top10_week.length > 0 && (
          <Top10Rail
            title={`Top 10 on ${tenant.name} this week`}
            items={top10_week}
            tenantSlug={slug}
            themeColor={themeColor}
            testid="storefront-top10-rail"
          />
        )}
        {new_releases && new_releases.length > 0 && (
          <ContentRow
            name="New releases"
            items={new_releases}
            tenantSlug={slug}
            themeColor={themeColor}
            kind="mixed"
          />
        )}
        {trending_movies && trending_movies.length > 0 && (
          <ContentRow
            name="Trending movies"
            items={trending_movies}
            tenantSlug={slug}
            themeColor={themeColor}
            kind="movie"
          />
        )}
        {reels_preview && reels_preview.length > 0 && (
          <section className="px-6 md:px-10 py-4" data-testid="storefront-reels-preview">
            <div className="flex items-end justify-between mb-3">
              <h2 className="font-display font-bold text-lg md:text-xl tracking-tight">Reels</h2>
              <Link to={sp(`/reels`)} className="text-[11px] uppercase tracking-[0.3em] hover:underline" style={{ color: accentColor }} data-testid="storefront-reels-see-all">
                See all →
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
              {reels_preview.slice(0, 8).map((r) => (
                <Link
                  key={r.id}
                  to={sp(`/r/${r.slug}`)}
                  className="snap-start flex-shrink-0 w-[140px] md:w-[160px] aspect-[9/16] relative bg-[#121212] border border-white/10 hover:border-white/30 overflow-hidden group"
                  data-testid={`storefront-reel-${r.slug}`}
                >
                  {r.thumbnail_url && (
                    <img src={r.thumbnail_url} alt={r.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
                  <div className="absolute bottom-2 inset-x-2 text-xs font-bold leading-tight line-clamp-2">{r.title}</div>
                </Link>
              ))}
            </div>
          </section>
        )}
        {genres.map((g) => (
          <ContentRow
            key={g.name}
            name={g.name}
            items={g.items}
            tenantSlug={slug}
            themeColor={themeColor}
            kind="genre"
          />
        ))}
      </div>

      {/* REASONS to subscribe */}
      <ReasonsGrid tenantName={tenant.name} accentColor={accentColor} themeColor={themeColor} />

      {/* FAQ */}
      <FAQAccordion items={faqs}>
        <div className="text-center" data-testid="faq-bottom-cta">
          <p className="text-white/65 text-sm md:text-base font-light mb-5">
            Ready to watch? Enter your email to create or restart your membership.
          </p>
          <div className="flex justify-center">
            <EmailCapture tenantSlug={slug} accentColor={accentColor} themeColor={themeColor} testid="storefront-faq-email-capture" />
          </div>
        </div>
      </FAQAccordion>

      <StickyBottomCTA
        tenantName={tenant.name}
        tenantSlug={slug}
        accentColor={accentColor}
        themeColor={themeColor}
        cheapestPriceLabel={cheapestLabel}
      />
      {!isOwnSubscriber && (
        <ExitIntentModal
          tenantSlug={slug}
          tenantName={tenant.name}
          themeColor={themeColor}
          accentColor={accentColor}
          cheapestPriceLabel={cheapestLabel}
        />
      )}
    </StorefrontLayout>
  );
}
